import { useMemo } from 'react'

import { create } from '@bufbuild/protobuf'
import type { ConnectError } from '@connectrpc/connect'
import { useQuery } from '@connectrpc/connect-query'
import {
  listSingularGroupNames,
  saveSingularGroupNames,
} from '@ebalance/proto/bffuser/v1/singular_day_service-SingularDayService_connectquery'
import {
  ListSingularGroupNamesRequestSchema,
  type ListSingularGroupNamesResponse,
} from '@ebalance/proto/bffuser/v1/singular_day_service_pb'
import type { UseQueryResult } from '@tanstack/react-query'

import {
  type InvalidatingMutationResultFor,
  useInvalidatingMutation,
} from '~/hooks/useInvalidatingMutation'
import { singularDayQueryKeys } from '~/pages/AdminSingularDay/hooks/queryKeys'
import { buildSingularGroupNameMap } from '~/pages/AdminSingularDay/models/options'

/**
 * 特異グループ表示名 (org 共通 1〜10 → name) の一覧を取得する (design Note
 * singular-group-names.md)。 命名済みグループのみ返る (未命名は欠落 →
 * client が既定名を補完、 `models/options.ts` の
 * `resolveSingularGroupName`)。
 */
export function useListSingularGroupNames(): UseQueryResult<
  ListSingularGroupNamesResponse,
  ConnectError
> {
  // 特異グループ名は画面の主役 (`ListSingularDays`) ではなく補助的な表示名。
  // global QueryClient の既定 `throwOnError: true` のままだと、 取得失敗が
  // render 中に throw して router の `errorElement` = 全画面エラーへ飛び、
  // 特異日一覧ごと消える。 補助 query は `false` を渡し、 失敗は `isError`
  // として consumer 側で扱う (`useListSingularDays` の JSDoc / `IssuerSelector`
  // 等と同じ fail-safe パターン)。
  return useQuery(
    listSingularGroupNames,
    create(ListSingularGroupNamesRequestSchema, {}),
    { throwOnError: false },
  )
}

/**
 * 特異グループ表示名の map (`buildSingularGroupNameMap`) + 取得失敗フラグを
 * 1 つに集約した hook (PR #6541 reviewer 指摘対応)。 表示名 map だけが必要な
 * consumer (`SingularDayTable` / `SingularDayForm` / 一覧ページ) はこちらを
 * 使い、 raw query を個別に呼んで map を組み直さない。
 *
 * `isError` を透過するので、 consumer 側 (一覧ページ) が「命名済みグループが
 * 取得失敗で既定名表示になっている」ことを Alert 等で明示できる
 * (`useListSingularGroupNames` の JSDoc のとおり失敗は fail-safe で
 * 既定名にフォールバックするだけなので、 失敗の可視化は consumer の責務)。
 * editor 用 (`GroupNamesModal` / `SingularGroupNameInlineEditor`) は
 * loading / snapshot 確定判定が別途必要なため raw `useListSingularGroupNames`
 * を直接使い続ける (対象外)。
 */
export function useSingularGroupNameMap(): {
  groupNames: ReadonlyMap<number, string>
  isError: boolean
} {
  const { data, isError } = useListSingularGroupNames()
  const groupNames = useMemo(
    () =>
      data !== undefined
        ? buildSingularGroupNameMap(data.singularGroupNames)
        : new Map<number, string>(),
    [data],
  )
  return { groupNames, isError }
}

/**
 * 特異グループ表示名の per-group upsert batch 保存 (design Note D2)。
 * entries の name 非空 → upsert、 空 → 既定名に戻す (行削除)。
 * 011c (インライン, 変更した 1 group のみ) / 011d (一括, 編集した group を送信)
 * の両方がこの mutation を呼ぶ (独立 save control、 design Note D7)。
 */
export function useSaveSingularGroupNames(): InvalidatingMutationResultFor<
  typeof saveSingularGroupNames
> {
  return useInvalidatingMutation(saveSingularGroupNames, [
    singularDayQueryKeys.groupNameList,
  ])
}
